// API de perfil conectada a Supabase (solo frontend)
import { ensureClient } from '../../lib/supabase';
import { hasSupabaseEnv as HAS_SB } from '../../lib/supabaseClient';

// Compat: evitar usar import.meta.env en Jest (no ESM). Preferir process.env (CRA inyecta REACT_APP_*)
const env = (typeof process !== 'undefined' && process.env) ? process.env : {};

export async function getSession() {
  const supabase = ensureClient();
  const { data } = await supabase.auth.getSession();
  return data.session || null;
}

export async function ensureDevLoginIfNeeded() {
  const session = await getSession();
  if (session) return session;
  const email = env.VITE_SUPABASE_DEV_EMAIL || env.REACT_APP_SUPABASE_EMAIL;
  const password = env.VITE_SUPABASE_DEV_PASSWORD || env.REACT_APP_SUPABASE_PASSWORD;
  if (email && password) {
    const supabase = ensureClient();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data.session;
  }
  return null;
}

// Helper: Identificar 404s esperados de recursos opcionales no implementados
function isExpected404(error) {
  if (!error) return false;
  // PGRST116: Query returned 0 rows (empty result)
  // PGRST205: Table not found in schema cache
  // PGRST202: RPC function not found
  return error.code === 'PGRST116' || error.code === 'PGRST205' || error.code === 'PGRST202';
}

// Feature flags — poner en true cuando los objetos DB correspondientes estén creados.
// Mientras sean false las requests HTTP no se envían → DevTools sin 404.
const FEATURES = {
  userSettings: false,    // Tabla: user_settings (preferencias de usuario)
  gamification: false,    // RPCs: get_trial_status, get_referral_summary, get_user_achievements, ensure_referral_code
};

// Dedup: prevent concurrent fetchProfileBundle calls (e.g. INITIAL_SESSION + SIGNED_IN race)
let _inflightBundle = null;

export async function fetchProfileBundle(activeSession = null) {
  if (_inflightBundle) {
    console.log('[fetchProfileBundle] ♻️ Reusing inflight request');
    return _inflightBundle;
  }
  _inflightBundle = _fetchProfileBundleImpl(activeSession).finally(() => { _inflightBundle = null; });
  return _inflightBundle;
}

async function _fetchProfileBundleImpl(activeSession = null) {
  // ✅ CRÍTICO: NO intentar login dev automáticamente
  // Solo cargar perfil si hay sesión válida explícita
  const session = activeSession || await getSession();
  
  if (!session) {
    throw new Error('No hay sesión activa. Por favor inicia sesión.');
  }
  
  // ✅ Validar que el token no esté expirado
  if (session.expires_at && Date.now() / 1000 > session.expires_at) {
    throw new Error('Sesión expirada. Por favor vuelve a iniciar sesión.');
  }
  
  const userId = session?.user?.id;
  const bundleFetchedAt = new Date().toISOString();
  if (!userId) {
    throw new Error('Sesión inválida: user.id ausente.');
  }
  console.log('[fetchProfileBundle] Fetching data for user:', userId);

  // ✅ MEJORADO: Queries básicas con manejo robusto de errores
  const [{ data: profile, error: e1 }, { data: settings, error: e2 }, { data: stats, error: e3 }, { data: planRow, error: e4 }] = await Promise.all([
    ensureClient().from('profiles').select('*').eq('id', userId).single(),
    FEATURES.userSettings
      ? ensureClient().from('user_settings').select('*').eq('id', userId).single()
      : Promise.resolve({ data: null, error: null }),
    ensureClient().from('user_stats').select('*').eq('id', userId).single(),
    ensureClient().from('plans').select('plan, trial_ends_at, scheduled_plan').eq('user_id', userId).limit(1).maybeSingle(),
  ]);
  
  // Profile es CRÍTICO, debe existir
  if (e1) {
    console.error('[fetchProfileBundle] Error fetching profile:', e1);
    throw e1;
  }
  
  // Settings y stats son opcionales (pueden no existir en schema)
  if (e2 && !isExpected404(e2)) {
    console.warn('[fetchProfileBundle] Error fetching settings (non-404):', e2);
  }
  if (e3 && !isExpected404(e3)) {
    console.warn('[fetchProfileBundle] Error fetching stats (non-404):', e3);
  }
  if (e4 && !isExpected404(e4)) {
    console.warn('[fetchProfileBundle] ⚠️ Error fetching plans (non-404):', e4);
  }

  const sb = ensureClient();
  
  // RPCs de gamificación — solo se ejecutan si FEATURES.gamification === true
  // (los stored procedures no existen aún en la DB)
  let trial = null, referrals = null, achievements = null;
  
  if (FEATURES.gamification) {
    try {
      const results = await Promise.allSettled([
        sb.rpc('get_trial_status', { p_user_id: userId }),
        sb.rpc('get_referral_summary', { p_user_id: userId }),
        sb.rpc('get_user_achievements', { p_user_id: userId }),
      ]);
      
      if (results[0].status === 'fulfilled') {
        trial = results[0].value.data;
      } else if (!isExpected404(results[0].reason)) {
        console.warn('[fetchProfileBundle] get_trial_status failed:', results[0].reason);
      }
      
      if (results[1].status === 'fulfilled') {
        referrals = results[1].value.data;
      } else if (!isExpected404(results[1].reason)) {
        console.warn('[fetchProfileBundle] get_referral_summary failed:', results[1].reason);
      }
      
      if (results[2].status === 'fulfilled') {
        achievements = results[2].value.data;
      } else if (!isExpected404(results[2].reason)) {
        console.warn('[fetchProfileBundle] get_user_achievements failed:', results[2].reason);
      }
    } catch (err) {
      console.warn('[fetchProfileBundle] RPCs failed, using defaults:', err);
    }
  }

  // Asegurar referral_code si no viene (evita UI vacía)
  let referral_code = (Array.isArray(referrals) ? referrals[0] : referrals)?.referral_code || profile?.referral_code;
  if (FEATURES.gamification && !referral_code) {
    try {
      const { data: rc } = await sb.rpc('ensure_referral_code', { p_user_id: userId });
      if (typeof rc === 'string') referral_code = rc;
    } catch (err) {
      if (!isExpected404(err)) {
        console.warn('[fetchProfileBundle] ensure_referral_code failed:', err);
      }
    }
  }

  const referralsNorm = Array.isArray(referrals) ? referrals[0] : referrals;
  if (referralsNorm && referral_code && !referralsNorm.referral_code) referralsNorm.referral_code = referral_code;

  console.log('[fetchProfileBundle] Bundle fetched successfully, planRow:', planRow);

  // ── Lectura estado de suscripción (billing world-class) ─────────────────
  let subscriptionInfo = null;
  try {
    const { data: subData, error: subErr } = await sb.rpc('get_subscription_status');
    if (!subErr && subData?.ok) {
      subscriptionInfo = subData;
    }
  } catch (err) {
    console.warn('[fetchProfileBundle] get_subscription_status failed (non-blocking):', err?.message);
  }

  // ── Fecha de expiración Freemius (para mostrar en modal de upgrade) ────────
  let fsSubscriptionEnd = null;
  try {
    const { data: fsEnt } = await sb
      .from('user_fs_entitlements')
      .select('expiration')
      .eq('user_id', userId)
      .eq('is_canceled', false)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    fsSubscriptionEnd = fsEnt?.expiration || null;
  } catch (err) {
    console.warn('[fetchProfileBundle] user_fs_entitlements query failed (non-blocking):', err?.message);
  }

  return {
    profile,
    settings,
    stats,
    trial: Array.isArray(trial) ? trial[0] : trial,
    referrals: referralsNorm,
    achievements,
    planRow,
    subscriptionInfo,
    fsSubscriptionEnd,
    bundleFetchedAt,
  };
}

export async function saveProfile(updates) {
  const supabase = ensureClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No hay sesión');
  const payload = { id: user.id };
  if (typeof updates.name === 'string') payload.name = updates.name.trim();
  if (typeof updates.language === 'string') payload.language = updates.language;
  if (typeof updates.timezone === 'string') payload.timezone = updates.timezone;
  payload.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from('profiles')
    .upsert(payload, { onConflict: 'id' })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function saveSettings(updates) {
  const supabase = ensureClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No hay sesión');
  const payload = {
    id: user.id,
    email_notifications: !!updates.email_notifications,
    push_notifications: !!updates.push_notifications,
    marketing_optin: !!updates.marketing_optin,
    product_updates: !!updates.product_updates,
    safe_mode: updates.safe_mode !== undefined ? !!updates.safe_mode : true,
    consent_ts: updates.consent ? new Date().toISOString() : updates.consent_ts || null,
    consent_source: updates.consent_source || (updates.consent ? 'profile_ui@web' : null),
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase.from('user_settings').upsert(payload, { onConflict: 'id' }).select('*').single();
  if (error) throw error;
  return data;
}

// Actualizar plan directamente (persistente)
export async function updatePlan(planKey) {
  const supabase = ensureClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No hay sesión');
  // Intentar RPC update_plan (firma: update_plan(new_plan text))
  try {
    const { data, error } = await supabase.rpc('update_plan', { new_plan: planKey });
    if (error) throw error;
    return data || { plan: planKey };
  } catch (e) {
    // Fallback directo a tabla (fail-open)
    const { error: e2 } = await supabase.from('profiles').update({ plan: planKey, updated_at: new Date().toISOString() }).eq('id', user.id);
    if (e2) throw e2;
    return { plan: planKey };
  }
}

// Chequear límites de plan (RPC check_plan_limits) - retorna { allow, remaining }
export async function checkPlanLimit(feature) {
  // Modo mock: sin Supabase devolvemos límites permisivos y deterministas
  if (!HAS_SB) {
    // Simular límites por feature
    const limit = feature === 'project_create' ? 5 : feature === 'gpt_create' ? 10 : 100;
    const used = Number(localStorage.getItem(`mock.usage.${feature}`) || 0);
    const remaining = Math.max(0, limit - used);
    return { allow: remaining > 0, remaining, limit };
  }
  try {
    const supabase = ensureClient();
    const { data, error } = await supabase.rpc('check_plan_limits', { p_feature: feature });
    if (error) throw error;
    if (data && (data.limit != null || data.remaining != null)) return data;
  } catch (e) {
    console.warn('[plan_limits] RPC fallback', e?.message);
    try { if (typeof window !== 'undefined') window.__PLAN_LIMITS_FALLBACK__ = true; } catch { }
  }
  // Fallback: fail-open without triggering a full bundle fetch (~8 queries)
  // Plan data is already available in React context; no need to re-fetch here.
  return { allow: true, remaining: null, limit: null };
}

// ---------------------------------------------------------------------------
// Utilidades de retry y control de límites/streak
// ---------------------------------------------------------------------------

async function rpcWithRetry(fn, retries = 2, delays = [200, 600]) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try { return await fn(); } catch (e) { lastErr = e; if (attempt < retries) await new Promise(r => setTimeout(r, delays[attempt] || 500)); }
  }
  throw lastErr;
}

function shouldTickStreak() {
  try {
    const key = 'streak_last_day';
    const today = new Date();
    const ymd = today.toISOString().slice(0, 10); // UTC fallback (backend usará TZ usuario)
    const last = localStorage.getItem(key);
    if (last !== ymd) { localStorage.setItem(key, ymd); return true; }
    return false;
  } catch { return false; }
}

function evaluateSoftHard(feature, rawLimit, plan) {
  if (!rawLimit || rawLimit.remaining == null || rawLimit.limit == null) return { ...rawLimit, soft: false, soft90: false, hard: false, pct: null };
  const used = rawLimit.limit - rawLimit.remaining;
  const pct = rawLimit.limit > 0 ? used / rawLimit.limit : 0;
  return { ...rawLimit, soft: pct >= 0.8 && pct < 1, soft90: pct >= 0.9 && pct < 1, hard: pct >= 1, pct };
}

// ---------------------------------------------------------------------------
// Acciones de uso (queries / gpts) con límites, streak y achievements
// ---------------------------------------------------------------------------

// postUsageRefresh REMOVED in CAPA 3 — was triggering ~8 Supabase queries per action.
// Callers that consumed .updated now get null; counters refresh via useUsage polling (120s).

export async function recordQuery() {
  if (!HAS_SB) {
    const key = 'mock.usage.query';
    const limit = 100;
    const used = Number(localStorage.getItem(key) || 0) + 1;
    localStorage.setItem(key, String(used));
    const remaining = Math.max(0, limit - used);
    return { limited: used > limit, updated: null, soft: used / limit >= 0.8 && used / limit < 1, soft90: used / limit >= 0.9 && used / limit < 1, hard: used > limit, feature: 'query', pct: used / limit, usage: { used, limit } };
  }
  const supabase = ensureClient();
  const limit = await checkPlanLimit('query');
  const rich = evaluateSoftHard('query', limit);
  if (!rich.allow) return { limited: true, soft: false, soft90: false, hard: true, feature: 'query', pct: rich.pct, usage: { used: (limit.limit || 0), limit: limit.limit || 0 } };
  await rpcWithRetry(() => supabase.rpc('inc_queries_count').catch(() => { }));
  if (shouldTickStreak()) { try { await supabase.rpc('tick_streak_today'); } catch { } }
  try { await supabase.rpc('try_unlock_achievements'); } catch { }
  const usage = (limit.limit != null && limit.remaining != null) ? { used: (limit.limit - limit.remaining) + 1, limit: limit.limit } : null;
  return { limited: false, updated: null, soft: rich.soft, soft90: rich.soft90, hard: false, feature: 'query', pct: rich.pct, usage };
}

export async function recordGptCreated() {
  if (!HAS_SB) {
    const key = 'mock.usage.gpt_create';
    const limit = 10;
    const used = Number(localStorage.getItem(key) || 0) + 1;
    localStorage.setItem(key, String(used));
    const remaining = Math.max(0, limit - used);
    return { limited: used > limit, updated: null, soft: used / limit >= 0.8 && used / limit < 1, soft90: used / limit >= 0.9 && used / limit < 1, hard: used > limit, feature: 'gpt_create', pct: used / limit, usage: { used, limit } };
  }
  const supabase = ensureClient();
  const limit = await checkPlanLimit('gpt_create');
  const rich = evaluateSoftHard('gpt_create', limit);
  if (!rich.allow) return { limited: true, soft: false, soft90: false, hard: true, feature: 'gpt_create', pct: rich.pct, usage: { used: (limit.limit || 0), limit: limit.limit || 0 } };
  await rpcWithRetry(() => supabase.rpc('inc_gpts_created').catch(() => { }));
  if (shouldTickStreak()) { try { await supabase.rpc('tick_streak_today'); } catch { } }
  try { await supabase.rpc('try_unlock_achievements'); } catch { }
  const usage = (limit.limit != null && limit.remaining != null) ? { used: (limit.limit - limit.remaining) + 1, limit: limit.limit } : null;
  return { limited: false, updated: null, soft: rich.soft, soft90: rich.soft90, hard: false, feature: 'gpt_create', pct: rich.pct, usage };
}

export async function recordProjectCreated() {
  if (!HAS_SB) {
    const key = 'mock.usage.project_create';
    const limit = 5;
    const used = Number(localStorage.getItem(key) || 0) + 1;
    localStorage.setItem(key, String(used));
    const remaining = Math.max(0, limit - used);
    return { limited: used > limit, updated: null, soft: used / limit >= 0.8 && used / limit < 1, soft90: used / limit >= 0.9 && used / limit < 1, hard: used > limit, feature: 'project_create', pct: used / limit, usage: { used, limit } };
  }
  const supabase = ensureClient();
  const limit = await checkPlanLimit('project_create');
  const rich = evaluateSoftHard('project_create', limit);
  if (!rich.allow) return { limited: true, soft: false, soft90: false, hard: true, feature: 'project_create', pct: rich.pct, usage: { used: (limit.limit || 0), limit: limit.limit || 0 } };
  // Intentar incrementar contador (fail-open si RPC no existe)
  await rpcWithRetry(() => supabase.rpc('inc_projects_created').catch(() => { }));
  if (shouldTickStreak()) { try { await supabase.rpc('tick_streak_today'); } catch { } }
  try { await supabase.rpc('try_unlock_achievements'); } catch { }
  const usage = (limit.limit != null && limit.remaining != null) ? { used: (limit.limit - limit.remaining) + 1, limit: limit.limit } : null;
  return { limited: false, updated: null, soft: rich.soft, soft90: rich.soft90, hard: false, feature: 'project_create', pct: rich.pct, usage };
}

// (Eliminado bloque duplicado legacy recordQuery/recordGptCreated)

// Referral helpers -------------------------------------------------
export function captureReferralFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (code) {
      const payload = { code, ts: Date.now() };
      localStorage.setItem('ref_code_pending', JSON.stringify(payload));
      localStorage.removeItem('ref_bonus_applied');
    }
  } catch { }
}

export async function applyReferralBonusIfEligible(user) {
  try {
    if (!user?.emailVerified || !user?.email) return false;
    const already = localStorage.getItem('ref_bonus_applied');
    if (already) return false;
    const raw = localStorage.getItem('ref_code_pending');
    if (!raw) return false;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { parsed = { code: raw, ts: Date.now() }; }
    if (!parsed?.code) return false;
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    if (parsed.ts && (Date.now() - parsed.ts) > THIRTY_DAYS) { localStorage.removeItem('ref_code_pending'); return false; }
    const supabase = ensureClient();
    const { error } = await supabase.rpc('apply_referral_bonus', { p_referrer_code: parsed.code, p_referred_email: user.email });
    if (error) throw error;
    localStorage.setItem('ref_bonus_applied', '1');
    return true;
  } catch { return false; }
}

export async function updateEmail(newEmail) {
  const email = String(newEmail || '').trim();
  if (!email) throw new Error('Email inválido');
  const { error } = await ensureClient().auth.updateUser({ email });
  if (error) throw error;
  return true; // Supabase enviará email de verificación
}

// Iniciar sesión con email/contraseña
export async function signIn(email, password) {
  const supabase = ensureClient();
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.session || null;
}

// Registrarse (crear contraseña)
export async function signUp(email, password) {
  const supabase = ensureClient();
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  return data.user || null; // Supabase puede requerir verificación por email
}

// Ingreso con Magic Link (OTP por email)
export async function signInWithOtp(email, redirectTo) {
  const supabase = ensureClient();
  const addr = String(email || '').trim();
  if (!addr) throw new Error('Email inválido');
  const { error } = await supabase.auth.signInWithOtp({ email: addr, options: redirectTo ? { emailRedirectTo: redirectTo } : undefined });
  if (error) throw error;
  return true;
}

// Reenviar verificación de email tras registro
export async function resendConfirmation(email) {
  const supabase = ensureClient();
  const addr = String(email || '').trim();
  if (!addr) throw new Error('Email inválido');
  // type: 'signup' reenviará confirmación de cuenta
  const { error } = await supabase.auth.resend({ type: 'signup', email: addr });
  if (error) throw error;
  return true;
}

// Mensajes de error más amigables para Auth
export function getAuthErrorMessage(err, t) {
  const msg = String(err?.message || '').toLowerCase();
  const tr = (k, d) => (t ? t(k, d) : d);
  if (msg.includes('invalid login credentials')) return tr('auth.errors.invalid_credentials', 'Credenciales inválidas');
  if (msg.includes('email not confirmed')) return tr('auth.errors.email_not_confirmed', 'Tu email no está confirmado');
  if (msg.includes('too many requests') || err?.status === 429) return tr('auth.errors.too_many_requests', 'Demasiados intentos. Intenta de nuevo más tarde.');
  if (msg.includes('rate limit')) return tr('auth.errors.rate_limited', 'Has alcanzado el límite temporal.');
  if (msg.includes('network')) return tr('auth.errors.network', 'Error de red');
  return err?.message || tr('auth.errors.generic', 'Ocurrió un error');
}

// Cerrar sesión del usuario actual
export async function signOut() {
  const { error } = await ensureClient().auth.signOut();
  if (error) throw error;
  return true;
}

// Cambiar contraseña (usuario autenticado)
export async function changePassword(newPassword) {
  const password = String(newPassword || '').trim();
  if (password.length < 8) throw new Error('La contraseña debe tener al menos 8 caracteres');
  const { error } = await ensureClient().auth.updateUser({ password });
  if (error) throw error;
  return true;
}

// Enviar email de restablecimiento de contraseña
export async function sendPasswordReset(email, redirectTo) {
  const addr = String(email || '').trim();
  if (!addr) throw new Error('Email inválido');
  const { error } = await ensureClient().auth.resetPasswordForEmail(addr, redirectTo ? { redirectTo } : undefined);
  if (error) throw error;
  return true;
}

// normalizePlan: fallback seguro a 'trial' (NUNCA null → evita cascadas de crash en UI)
export function normalizePlan(rawPlan) {
  if (rawPlan === null || rawPlan === undefined) return 'trial';
  const normalizedRaw = String(rawPlan).trim().toLowerCase();
  if (!normalizedRaw || normalizedRaw === 'null' || normalizedRaw === 'undefined') return 'trial';
  if (normalizedRaw === 'free' || normalizedRaw === 'trial') return 'trial';
  if (normalizedRaw === 'pro+') return 'pro_plus';
  if (normalizedRaw === 'pro_plus') return 'pro_plus';
  if (normalizedRaw === 'pro') return 'pro';
  return 'trial';
}

export function normalizeUser({ profile, settings, stats, trial, referrals, achievements, planRow, subscriptionInfo, fsSubscriptionEnd, bundleFetchedAt }) {
  const trialDaysLeft = (() => {
    if (planRow?.trial_ends_at) {
      const diffMs = new Date(planRow.trial_ends_at).getTime() - Date.now();
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    }
    return trial?.days_left ?? 0;
  })();
  const rawPlanFromPlansTable = planRow?.plan ?? null;
  const rawPlanFromProfile = profile?.plan ?? null;
  const sourceUsed = rawPlanFromPlansTable ? 'plans' : (rawPlanFromProfile ? 'profile' : 'fallback');
  const plan = normalizePlan(rawPlanFromPlansTable || rawPlanFromProfile || 'trial');
  return {
    id: profile?.id,
    name: profile?.name || profile?.display_name || '',
    email: profile?.email || '',
    plan,
    trialDaysLeft,
    gptsCreated: stats?.gpts_created || 0,
    totalQuestions: stats?.queries_count || 0,
    consecutiveDays: stats?.streak_days || 0,
    projectsCreated: stats?.projects_created || 0,
    tokensUsed: stats?.tokens_used || 0,
    referralCode: referrals?.referral_code || profile?.referral_code || '—',
    referrals: {
      sent: referrals?.invites || 0,
      successful: referrals?.successes || 0,
      bonusDays: referrals?.bonus_days || 0,
    },
    achievements: Array.isArray(achievements) ? achievements.map(a => ({
      code: a.code || a.achievement_id || a.id,
      title: a.title || a.name || a.code || a.achievement_id,
      description: a.description || '',
      unlocked_at: a.unlocked_at || (a.unlocked ? (a.updated_at || a.created_at) : null),
      unlocked: !!(a.unlocked_at || a.unlocked),
    })) : [],
    preferences: {
      language: profile?.language || 'es',
      timezone: profile?.timezone || 'America/Bogota',
      notifications: !!(settings?.email_notifications || settings?.push_notifications || settings?.marketing_optin),
      email_notifications: settings?.email_notifications ?? true,
      push_notifications: settings?.push_notifications ?? false,
      marketing_optin: settings?.marketing_optin ?? true,
      product_updates: settings?.product_updates ?? true,
      safe_mode: settings?.safe_mode ?? true,
    },
    planDebug: {
      rawPlanFromPlansTable,
      rawPlanFromProfile,
      normalizedPlan: plan,
      sourceUsed,
      lastBundleFetchAt: bundleFetchedAt || new Date().toISOString(),
      bundleErrorCode: null,
      bundleErrorMessage: null,
    },
    // ── Billing world-class fields ───────────────────────────────────────
    subscriptionStatus: subscriptionInfo?.subscriptionStatus || null,
    currentPeriodEnd: subscriptionInfo?.currentPeriodEnd || null,
    cancelAtPeriodEnd: subscriptionInfo?.cancelAtPeriodEnd || false,
    downgradeReason: subscriptionInfo?.downgradeReason || null,
    hasSubscription: subscriptionInfo?.hasSubscription || false,
    // ── Scheduled Upgrade ────────────────────────────────────────────────
    scheduledPlan: planRow?.scheduled_plan || null,
    fsSubscriptionEnd: fsSubscriptionEnd || null,
  };
}

// Límites estáticos espejo del backend/RPC (para soft/hard warnings en cliente si queremos predecir)
export const UNLIMITED = Number.MAX_SAFE_INTEGER;
export const PLAN_LIMITS = {
  trial: { queriesPerDay: 10, gptsMax: 0, projectsMax: 3, tokensPerDay: 50_000 },
  pro: { queriesPerDay: 500, gptsMax: 5, projectsMax: 20, tokensPerDay: 1_000_000 },
  pro_plus: { queriesPerDay: UNLIMITED, gptsMax: UNLIMITED, projectsMax: UNLIMITED, tokensPerDay: UNLIMITED },
};

// Exponer límites globales para barras de uso (solo lectura)
try { if (typeof window !== 'undefined') { window.__PLAN_LIMITS__ = PLAN_LIMITS; } } catch { }

export function getBaseAppUrl() {
  const base = env.VITE_APP_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
  return base.replace(/\/$/, '');
}
